/**
 * BroadcastsPage — `/franchise/broadcasts`.
 *
 * Wraps `GET /v1/franchise/bulletins` (admin list) and `POST /v1/franchise/bulletins`
 * (publish). Lets the operator publish a new bulletin with subject / body /
 * priority / target tiers / expiry, and lists the recent bulletins.
 *
 * Per-franchise read-receipt counts are aggregated by the operations slice
 * (`GET /v1/franchise/ops/bulletin-receipts`) and rendered alongside each
 * bulletin row.
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  listAllBulletins,
  listBulletinReceiptSummaries,
  publishBulletin,
  type BulletinPriority,
  type TierCode,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

const PRIORITIES: readonly BulletinPriority[] = ['INFO', 'WARN', 'URGENT'];
const TIERS: readonly TierCode[] = ['TIER_1', 'TIER_2', 'TIER_3'];

export function BroadcastsPage(): JSX.Element {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState<BulletinPriority>('INFO');
  const [tiers, setTiers] = useState<readonly TierCode[]>([]);
  const [expiresAt, setExpiresAt] = useState('');

  const list = useQuery({
    queryKey: ['franchise', 'bulletins', 'all'],
    queryFn: () => listAllBulletins(),
    retry: false,
  });

  const receipts = useQuery({
    queryKey: ['franchise', 'bulletins', 'receipts'],
    queryFn: () => listBulletinReceiptSummaries(),
    retry: false,
  });

  const denied =
    list.isError && list.error instanceof ApiError &&
    (list.error.status === 401 || list.error.status === 403);

  const receiptByBulletin = new Map(
    (receipts.data?.items ?? []).map((r) => [r.bulletinId, r]),
  );

  const totalAck = (receipts.data?.items ?? []).reduce(
    (acc, r) => acc + r.acknowledgedCount,
    0,
  );
  const totalTargets = (receipts.data?.items ?? []).reduce(
    (acc, r) => acc + r.targetCount,
    0,
  );
  const overallAckRate = totalTargets === 0 ? '0.00' : ((totalAck / totalTargets) * 100).toFixed(2);

  const publish = useMutation({
    mutationFn: () => {
      if (subject.length === 0 || body.length === 0) {
        throw new Error('Subject and body are required.');
      }
      return publishBulletin({
        subject,
        body,
        priority,
        ...(tiers.length > 0 ? { targetTiers: tiers } : {}),
        ...(expiresAt.length > 0 ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
      });
    },
    onSuccess: () => {
      setSubject('');
      setBody('');
      setPriority('INFO');
      setTiers([]);
      setExpiresAt('');
      setShowCreate(false);
      void qc.invalidateQueries({ queryKey: ['franchise', 'bulletins', 'all'] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const toggleTier = (t: TierCode): void => {
    setTiers((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Broadcasts"
        description="Publish bulletins to franchise outlets."
        actions={
          <button
            type="button"
            onClick={() => setShowCreate((v) => !v)}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark"
          >
            {showCreate ? 'Cancel' : 'New broadcast'}
          </button>
        }
      />

      <div
        data-testid="broadcast-ack-summary"
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <KPICard title="Bulletins" value={String(list.data?.items.length ?? 0)} />
        <KPICard
          title="Acknowledged"
          value={`${totalAck} / ${totalTargets}`}
          hint="Sum across all bulletins"
        />
        <KPICard title="Acknowledgement rate" value={`${overallAckRate}%`} />
      </div>

      {showCreate ? (
        <div className="bg-white border border-border rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-gray-900">New broadcast</p>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Body</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            />
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as BulletinPriority)}
                className="px-3 py-2 border border-border rounded-lg text-sm"
              >
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Expires</label>
              <input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            <div className="flex items-center gap-3">
              {TIERS.map((t) => (
                <label key={t} className="flex items-center gap-1 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={tiers.includes(t)}
                    onChange={() => toggleTier(t)}
                  />
                  {t}
                </label>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                setActionErr(null);
                publish.mutate();
              }}
              disabled={publish.isPending}
              className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
            >
              {publish.isPending ? 'Publishing…' : 'Publish'}
            </button>
          </div>
        </div>
      ) : null}

      {actionErr ? <p className="text-sm text-danger">{actionErr}</p> : null}

      {list.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">FranchiseBulletin/read</code> required.
        </DeferredNotice>
      ) : list.isError ? (
        <DeferredNotice title="Service unreachable">{(list.error as Error).message}</DeferredNotice>
      ) : (list.data?.items.length ?? 0) === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No bulletins published yet.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">Subject</th>
                <th className="text-left px-4 py-2">Priority</th>
                <th className="text-left px-4 py-2">Targets</th>
                <th className="text-left px-4 py-2">Acknowledged</th>
                <th className="text-left px-4 py-2">Published</th>
                <th className="text-left px-4 py-2">Expires</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {list.data?.items.map((b) => {
                const tt = Array.isArray(b.targetTiers) ? (b.targetTiers as string[]) : [];
                const ff = Array.isArray(b.targetFranchiseIds)
                  ? (b.targetFranchiseIds as string[])
                  : [];
                const targets = [
                  tt.length > 0 ? tt.join(', ') : null,
                  ff.length > 0 ? `${ff.length} franchise(s)` : null,
                ]
                  .filter(Boolean)
                  .join(' · ') || 'All';
                const ack = receiptByBulletin.get(b.id);
                return (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-xs">
                      <span className="block font-medium text-gray-900">{b.subject}</span>
                      <span className="text-[11px] text-muted line-clamp-1">{b.body}</span>
                    </td>
                    <td className="px-4 py-2">
                      <StatusBadge status={b.priority} />
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-700">{targets}</td>
                    <td className="px-4 py-2 text-xs text-gray-700" data-testid={`ack-cell-${b.id}`}>
                      {ack
                        ? `${ack.acknowledgedCount} / ${ack.targetCount} (${ack.ackRate}%)`
                        : '—'}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted">
                      {new Date(b.publishedAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted">
                      {b.expiresAt ? new Date(b.expiresAt).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
